import React, { useState, useRef, useLayoutEffect, useCallback } from "react";
import ReactDOM from "react-dom";
import { X, ChevronDown } from "lucide-react";

interface SelectedSpacesDrawerProps {
  selectedSpacesChips: string[];
  onRemoveSpaceChip: (spaceId: string) => void;
  onClearAllSpaceChips: () => void;
  plansInfo?: any;
}

// Popover width (px). Kept in sync with the `w-64` class so the fixed-position
// portal can align the popover's right edge to the trigger button.
const POPOVER_WIDTH = 256;

const SelectedSpacesDrawer: React.FC<SelectedSpacesDrawerProps> = ({
  selectedSpacesChips,
  onRemoveSpaceChip,
  onClearAllSpaceChips,
  plansInfo,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [coords, setCoords] = useState<{ top: number; left: number } | null>(
    null
  );

  // If selectedSpacesChips is undefined, default to empty array
  const chips = selectedSpacesChips || [];
  const hasChips = chips.length > 0;

  const getSpaceLabel = (id: string) => {
    if (!plansInfo?.spaces) return id;

    const space = plansInfo.spaces.find(
      (s: any) =>
        s.spaceNumber?.toLowerCase() === id.toLowerCase() ||
        s.spaceNumber?.replaceAll("_", " ").toLowerCase() === id.replaceAll("_", " ").toLowerCase()
    );

    if (space && space.spaceHeader && space.spaceHeader !== space.spaceNumber) {
      return `${id} - ${space.spaceHeader}`;
    }
    return id;
  };

  // Position the popover with fixed coordinates derived from the trigger button.
  // Rendering it in a portal (instead of an absolutely-positioned child) avoids
  // being clipped by the drawer's `overflow: hidden` container and any z-index
  // stacking issues, in both the docked and popped-out (PiP) windows.
  const updatePosition = useCallback(() => {
    const el = buttonRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const view = el.ownerDocument.defaultView || window;
    // Align the popover's right edge to the button, clamped into the viewport.
    const left = Math.max(
      8,
      Math.min(rect.right - POPOVER_WIDTH, view.innerWidth - POPOVER_WIDTH - 8)
    );
    setCoords({ top: rect.bottom + 4, left });
  }, []);

  useLayoutEffect(() => {
    if (!isOpen) return;
    updatePosition();
    const view = buttonRef.current?.ownerDocument.defaultView || window;
    view.addEventListener("resize", updatePosition);
    view.addEventListener("scroll", updatePosition, true);
    return () => {
      view.removeEventListener("resize", updatePosition);
      view.removeEventListener("scroll", updatePosition, true);
    };
  }, [isOpen, updatePosition]);

  const portalTarget =
    isOpen && buttonRef.current ? buttonRef.current.ownerDocument.body : null;

  return (
    <div className="relative">
      <button
        ref={buttonRef}
        onClick={() => setIsOpen(!isOpen)}
        title={!hasChips ? "Use tools to Select Spaces" : ""}
        className={`flex items-center gap-1.5 text-slate-600 font-medium transition py-1 px-2 rounded-md hover:text-blue-600 hover:bg-slate-50 ${isOpen ? 'bg-blue-50 text-blue-600' : ''}`}
      >
        <span className="flex items-center justify-center text-white text-[10px] font-bold h-4 min-w-[16px] px-1 rounded-full shadow-sm bg-blue-600">
          {chips.length}
        </span>
        <span className="whitespace-nowrap">Spaces Selected</span>
        <ChevronDown size={12} />
      </button>

      {portalTarget &&
        ReactDOM.createPortal(
          <>
            <div
              className="fixed inset-0"
              style={{ zIndex: 2000000000 }}
              onClick={() => setIsOpen(false)}
            ></div>
            <div
              className="fixed w-64 bg-white rounded-lg shadow-xl border border-slate-100 py-2 px-3 animate-in fade-in zoom-in-95 duration-100"
              style={{
                top: coords?.top ?? 0,
                left: coords?.left ?? 0,
                maxWidth: "calc(100vw - 16px)",
                zIndex: 2000000001,
              }}
            >
              {!hasChips ? (
                <div className="py-2 flex items-center justify-center text-xs font-medium text-blue-600 bg-blue-50 rounded-md border border-blue-100">
                  Use tools to select spaces
                </div>
              ) : (
                <>
                  <div className="flex items-center justify-between mb-2 pb-2 border-b border-slate-100">
                    <span className="text-[10px] uppercase font-bold text-slate-400 tracking-wider">
                      Selected Spaces
                    </span>
                    <button
                      onClick={() => {
                        onClearAllSpaceChips();
                        setIsOpen(false);
                      }}
                      className="text-[10px] text-red-500 font-semibold hover:text-red-700 hover:underline"
                    >
                      Clear All
                    </button>
                  </div>
                  <div className="flex flex-wrap gap-1.5 max-h-48 overflow-y-auto pr-1">
                    {chips.map((id, index) => (
                      <div
                        key={`${id}-${index}`}
                        className="flex-shrink-0 flex items-center gap-1 bg-white border border-blue-100 text-blue-600 px-1.5 py-0.5 rounded text-[10px] font-mono shadow-sm"
                      >
                        {getSpaceLabel(id)}
                        <button
                          onClick={() => onRemoveSpaceChip(id)}
                          className="hover:text-red-500"
                        >
                          <X size={8} />
                        </button>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          </>,
          portalTarget
        )}
    </div>
  );
};

export default SelectedSpacesDrawer;
